import { CellClickedEvent, ColDef } from "ag-grid-community";
import { useMemo } from "react";
import type { FhirSectionProps } from "../fhir-section";
import FhirSection from "../fhir-section";
import { createConditionsFilterV1 } from "./filter-utils";
import { ConditionRowData } from "./table-data";

interface ConditionRowDataWithHccSortValue extends ConditionRowData {
  hccSortValue: number;
}
function ConditionsSection(props: FhirSectionProps) {
  const defaultColDef: ColDef = {
    flex: 1,
    onCellClicked: (event: CellClickedEvent) => props.handleCellClick(props.sectionId, event),
    cellStyle: props.handleCellStyle,
  };

  const filters = useMemo(() => createConditionsFilterV1(), []);

  const enhancedRowData = useMemo(() => {
    return props.rowData.map(data => ({
      ...data,
      hccSortValue: extractHccSortValue((data as ConditionRowData).code),
    }));
  }, [props.rowData]);

  const customColumnDefs = useMemo(() => {
    return props.columnDefs?.map(colDef => {
      if (colDef.field === "code") {
        return {
          ...colDef,
          comparator: (
            valueA: string,
            valueB: string,
            nodeA: { data: ConditionRowDataWithHccSortValue },
            nodeB: { data: ConditionRowDataWithHccSortValue }
          ) => {
            const { isHcc: isHccA, hccSortValue: sortA } = nodeA.data;
            const { isHcc: isHccB, hccSortValue: sortB } = nodeB.data;
            if (isHccA && isHccB) return (sortB || 0) - (sortA || 0);
            if (isHccA && !isHccB) return -1;
            if (!isHccA && isHccB) return 1;
            return valueA?.localeCompare(valueB) || 0;
          },
        };
      }
      return colDef;
    });
  }, [props.columnDefs]);

  return (
    <FhirSection
      {...props}
      rowData={enhancedRowData}
      columnDefs={customColumnDefs || props.columnDefs}
      defaultColDef={defaultColDef}
      filterToggles={filters}
    />
  );
}

// custom comparator for HCC conditions
function extractHccSortValue(codeString: string): number {
  const hccMatch = codeString.match(/HCC:\s*(.+?)(?:$|;)/);
  if (!hccMatch) return 0;

  const hccCodes = hccMatch[1];

  // Find versioned codes like "21 (v28)" and simple codes like "88"
  const versionedMatches = Array.from(hccCodes?.matchAll(/(\d+)\s*\(v(\d+)\)/g) || []);
  const simpleMatches = Array.from(hccCodes?.matchAll(/(?:^|[,;]\s*)(\d+)(?!\s*\()/g) || []);

  const allCodes: number[] = [];

  // Extract all versioned codes
  versionedMatches.forEach(match => {
    const code = parseInt(match[1] || "") || 0;
    if (code > 0) allCodes.push(code);
  });

  // Extract all simple codes (not followed by version)
  simpleMatches.forEach(match => {
    const code = parseInt(match[1] || "") || 0;
    if (code > 0) allCodes.push(code);
  });

  // Return the highest code across all versions and formats
  return allCodes.length > 0 ? Math.max(...allCodes) : 0;
}

export default ConditionsSection;
